import React, { FunctionComponent, memo, useContext } from "react";
import { useTranslation } from "react-i18next";

import { Header } from "@/components/Header";
import { Icon } from "@/components/Icon";
import { SubSection } from "@/components/Section";
import { Separator } from "@/components/Separator";
import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import { agoDate } from "@/core/dates";
import { IJobApplication } from "@/data/job-applications";
import { formatFullName } from "@/data/persons";
import { Flex } from "@/eworkitComponents";
import { useCodelistTranslation } from "@/hooks/Codelist";
import { useFormats } from "@/hooks/Formats";
import { RouteSection } from "@/infrastructure/Route";
import { JobApplicationStatus } from "@/views/JobApplications/Detail/components/JobApplicationStatus";
import { router } from "@/views/router";

import { JobRequestApplicationRecordDetailSection } from "./JobRequestApplicationRecordDetailSection";

interface Props {
  record: IJobApplication;
}

export const JobRequestApplicationRecord: FunctionComponent<Props> = memo(({ record }) => {
  const { t } = useTranslation("session");
  const tCodelist = useCodelistTranslation();
  const { formatDate, formatRate } = useFormats();

  const User = useContext(UserContext);
  const isUser = User.hasConsultantProfile(record.consultantProfile.id);
  const profileName = `${formatFullName(record.consultantProfile)} ${isUser ? "(you)" : ""}`;

  return (
    <RouteSection path={router.get("job-applications.details", { jobApplicationId: record.id }).path} flex borderRadius>
      <SubSection role="top" padding={["md"]} innerMargin="sm">
        <Flex direction="row">
          <Flex grow={1} direction="column">
            <Header size="sm" text={profileName} weight="bold" className="-word-break-all" hjSuppress />
            <Flex direction="row" innerMargin="xs">
              <Text color="grey2" text={record.consultantProfile.position.name || ""} />
            </Flex>
          </Flex>
          <Flex grow={0} direction="column" justify="center">
            <JobApplicationStatus status={record.status} />
          </Flex>
        </Flex>
        <Separator />
        <Flex direction="column" innerMargin="sm">
          <JobRequestApplicationRecordDetailSection
            label={t("CONSULTANT_PROFILES.DETAIL.AVAILABILITY.NOTICE_PERIOD_LABEL")}
            value={
              record.consultantProfile.availabilityDate
                ? formatDate(record.consultantProfile.availabilityDate)
                : tCodelist("availability", record.consultantProfile.availability)
            }
          />
          {record.rate && (
            <JobRequestApplicationRecordDetailSection
              label={t("JOB_APPLICATIONS.DETAIL.SECTION_DETAILS.HOURLY_RATE")}
              value={formatRate(
                record.rate.ratePerHour,
                record.rate.currency,
                record.jobRequest.rate.clientRateType === "HOURLY" ? "h" : undefined,
              )}
            />
          )}
        </Flex>
      </SubSection>
      <SubSection role="bottom" padding={["md"]} innerPadding="md" innerMargin="md" background="background-card-bottom">
        <Flex direction="row">
          <Flex grow={1} innerPadding="xs">
            <Icon icon="messages" color="grey2" size="md" />
            <Text color="grey2" text={formatFullName(record.consultantProfile.contactPerson)} hjSuppress />
          </Flex>
          <Flex grow={0}>
            <Text color="grey2" text={agoDate(record.createdDate)} />
          </Flex>
        </Flex>
      </SubSection>
    </RouteSection>
  );
});
